import {Button, Card, Divider, Stack, Typography, useTheme} from '@mui/material';
import {MainHeader, PageWrapperLayout, StyledGrid} from 'presentation/components';
import {useBeneficiaryCard, useHostName} from 'presentation/hooks';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {translations} from 'presentation/translations-new';
import React from 'react';
import {useNavigate} from 'react-router-dom';
import {useAccountInformation, useMediaQueries} from '../hooks';
import {StyledCard} from './styles';
import {PORTAL_URL} from './constants';
import {removeAuthTokens} from 'infra/http/utils';
import {clearLocalStorageFeatureFlags} from 'infra/http/utils/clear-local-storage-feature-flags';
import {useThemeStore} from 'presentation/store/useThemeStore';
import {useQueryClient} from '@tanstack/react-query';
import {SelectionFlowEnum, SignInEnum} from 'presentation/constants';
import {usePlanSelection} from 'presentation/pages/others/selection-flow/pages/plan-selection/hooks/usePlanSelection';
import {clearCongenereEligibilityStorage} from 'infra/http/utils/clear-congenere-eligibility-storage';
import {useFacialBiometricsStore} from 'presentation/modules/booking/shared/store/useFacialBiometricsStore';

const {
	pageTitle,
	notredameTitle,
	gndiTitle,
	subtitle,
	primaryButton,
	secondaryButton,
	planCard,
	backToLogin,
} = translations['pt-br'].privateFlow.accessPortal;

export function AccessPortal() {
	const {isMobile} = useMediaQueries();
	const navigate = useNavigate();
	const {palette} = useTheme();
	const {beneficiaryCardPlanData} = useBeneficiaryCard();
	const {beneficiary, beneficiariesLength} = useAccountDataStore(state => state.accountData);
	const resetTheme = useThemeStore(state => state.resetTheme);
	const resetFacialBiometrics = useFacialBiometricsStore(state => state.resetFacialBiometrics);
	const queryClient = useQueryClient();
	const {isNotredameSaude} = useHostName();
	const {isPPOPlan} = useAccountInformation();
	const {uniquePlan} = usePlanSelection();

	const url = isNotredameSaude && !isPPOPlan ? PORTAL_URL.GNDI : PORTAL_URL.NOTREDAME;
	const title = isNotredameSaude && !isPPOPlan ? gndiTitle : notredameTitle;
	const isUniquePlan = !!uniquePlan;

	function logout() {
		removeAuthTokens();
		clearLocalStorageFeatureFlags();
		clearCongenereEligibilityStorage();
		resetFacialBiometrics();
		resetTheme();
		queryClient.clear();
	}

	function handleBack() {
		if (isUniquePlan) {
			logout();
			window.location.href = SignInEnum.LOGIN;
			return;
		}
		navigate(SelectionFlowEnum.PLAN_SELECTION);
	}

	function handleAccessPortal() {
		logout();
		window.location.href = url;
	}

	return (
		<PageWrapperLayout title={pageTitle}>
			<MainHeader title={pageTitle} hideProfileMenu />
			<StyledGrid.Root p={isMobile ? 2 : 4} spacing={3}>
				<StyledGrid.Item sm={12} md={12} lg={8} order={{xs: 2, md: 1}}>
					<StyledCard elevation={0}>
						<Stack alignItems="center" gap={0.5} width={450}>
							<Typography variant="h6" textAlign="center">
								{title}
							</Typography>
							<Typography variant="body1" textAlign="center" mb={1.5}>
								{subtitle}
							</Typography>
							<Button
								fullWidth
								variant="contained"
								onClick={handleAccessPortal}
								sx={{borderRadius: 8, mb: 0.5}}>
								{primaryButton}
							</Button>
							<Button
								fullWidth
								variant="outlined"
								onClick={handleBack}
								sx={{borderRadius: 8}}>
								{isUniquePlan ? backToLogin : secondaryButton}
							</Button>
						</Stack>
					</StyledCard>
				</StyledGrid.Item>
				<StyledGrid.Item sm={12} md={4} lg={4} order={{xs: 1, md: 2}}>
					<Card elevation={0} sx={{p: 2}}>
						<Typography variant="h6" color={palette.primary.main} fontWeight={600}>
							{planCard.title}
						</Typography>
						<Typography variant="body2" color={palette.primary.main} mb={1.5}>
							{beneficiaryCardPlanData.userName}
						</Typography>
						<Divider sx={{mb: 1.5}} />
						<Stack direction="row" spacing={4}>
							<Stack>
								<Typography variant="caption">{planCard.contractNumber}</Typography>
								<Typography variant="body2" color="text.secondary">
									{beneficiary.nuContrato}
								</Typography>
							</Stack>
							<Stack>
								<Typography variant="caption">{planCard.beneficiaries}</Typography>
								<Typography variant="body2" color="text.secondary">
									{beneficiariesLength}
								</Typography>
							</Stack>
						</Stack>
					</Card>
				</StyledGrid.Item>
			</StyledGrid.Root>
		</PageWrapperLayout>
	);
}
